/**
 * Pure engraving pricing/character logic (issue #22).
 *
 * Deliberately dependency-free and framework-free so it can run both as a
 * plain browser <script> (attaches `window.EngravingLogic`, loaded before
 * engraving-modal.js) and under Node's built-in test runner via `require`
 * (see engraving-logic.test.js). No bundler exists in this theme, so this
 * file uses a tiny UMD-style export instead of ES module syntax.
 */
(function (root) {
  /**
   * Grapheme-safe character count for billing (06-pricing-cart/README.md).
   * `text.length` counts UTF-16 code units, so a surrogate pair (an emoji,
   * any astral-plane input) would count as 2 and could tip a 5-character
   * engraving into the wrong tier. `Array.from` iterates by code point.
   * Not trimmed: a space is billed as 1 character regardless of position —
   * leading, trailing, or between characters — per the same doc.
   */
  function countCharacters(text) {
    return Array.from(text || '').length;
  }

  /**
   * The tier whose [min,max] contains `count`. Falls back to the last tier
   * when count runs past every band (06-pricing-cart/README.md: "fail
   * closed" is handled one level up by canCommitEngraving — this fallback
   * exists only so a price can still be *displayed* while text is over the
   * limit, never so an over-length engraving can actually be committed).
   * Returns null for count <= 0 or an empty tier list.
   */
  function resolveTier(tiers, count) {
    if (!count || count <= 0) return null;
    var list = tiers || [];
    if (!list.length) return null;
    for (var i = 0; i < list.length; i++) {
      var t = list[i];
      if (count >= t.min && count <= t.max) return t;
    }
    return list[list.length - 1];
  }

  /**
   * Whether the current draft can legally be committed: non-empty, at or
   * under the character cap, and a real tier resolves for it. This is the
   * single gate the "Add Engraving" CTA and the insert-symbol buttons both
   * rely on to guarantee the buyer is never charged the wrong fee (or no
   * fee at all) for what they typed.
   */
  function canCommitEngraving(text, maxCharacters, tiers) {
    if (!(text || '').trim()) return false;
    var count = countCharacters(text);
    if (count > maxCharacters) return false;
    return !!resolveTier(tiers, count);
  }

  /** Would inserting at the caret push the draft over the character cap? */
  function wouldExceedMax(nextText, maxCharacters) {
    return countCharacters(nextText) > maxCharacters;
  }

  /**
   * The draft cut to the character cap, counted the same way billing counts.
   *
   * `maxlength` on the input stops typing but NOT a paste that ignores it, and
   * an over-length draft is visibly broken rather than merely rejected: the
   * counter reads "-8 Characters Remaining" and the preview draws a string the
   * template can never hold. `canCommitEngraving` still refuses it, so it was
   * never a revenue leak — this exists so the buyer is shown a legal draft
   * instead of an illegal one (QA row F5).
   *
   * Slices by CODE POINT, like countCharacters: `String.slice` would cut a
   * surrogate pair in half and leave a lone surrogate in the middle of a
   * buyer's engraving. Returns the input untouched when it already fits, so
   * the caller's `$watch` cannot loop.
   */
  function truncateToMax(text, maxCharacters) {
    var value = text || '';
    if (!(maxCharacters > 0)) return value;
    var points = Array.from(value);
    if (points.length <= maxCharacters) return value;
    return points.slice(0, maxCharacters).join('');
  }

  /**
   * Emoji, skin-tone modifiers, flags and the joiners that build them.
   *
   * An emoji cannot be engraved: it is a colour, multi-path glyph no engraving
   * face carries, so it draws as tofu in the preview and reaches the workshop
   * as a character nobody can cut. It also costs a full character of a 12-slot
   * budget while showing the buyer nothing.
   *
   * What this matches:
   * - `\p{Extended_Pictographic}` — every pictograph, whether it defaults to
   *   emoji presentation or to text. This branch is what catches the macOS
   *   emoji picker's ❤️ (U+2764 U+FE0F): stripping the variation selector alone
   *   left the bare U+2764, a heart that is NOT the ♡ tile and that no
   *   engraving face draws the same way. It leaves ordinary punctuation,
   *   currency, arrows and maths alone — those carry no pictographic property.
   * - `\p{Emoji_Presentation}` — redundant with the above for every character
   *   Unicode currently assigns, kept because the two properties are maintained
   *   separately and a future addition need only land in one of them.
   * - `\p{Emoji_Modifier}` — the five skin tones, which trail their base.
   * - `\p{Regional_Indicator}` — flags are two of these and nothing else.
   * - `U+FE0F` / `U+FE0E` / `U+20E3` / `U+200D` — the two variation selectors
   *   (emoji and text presentation), the keycap combiner, and the ZWJ that welds
   *   a family or profession sequence together. Stripping these is what turns
   *   `1️⃣` back into the engravable digit `1`.
   * - `U+E0020`-`U+E007F`, the **tag characters**. These are the only INVISIBLE
   *   leak, and they are why this branch exists: the England / Scotland / Wales
   *   flags are `U+1F3F4` plus five or six tag characters, and tag characters
   *   carry no emoji property at all. Removing only the pictographic base left
   *   six invisible code points in the draft — a field that looks empty, has
   *   spent half of a twelve-character budget, and bills at the higher tier.
   *
   * `\p{Emoji}` is NOT in the list and must not be added: it is true for the
   * digits 0-9, `#` and `*`, because those are keycap bases. It would delete
   * every date a buyer engraved.
   */
  var EMOJI_RE = /\p{Extended_Pictographic}|\p{Emoji_Presentation}|\p{Emoji_Modifier}|\p{Regional_Indicator}|[️︎⃣‍]|[\u{E0020}-\u{E007F}]/gu;

  /**
   * Pictographs that are exempt, because they are engravable and wanted.
   *
   * The first six are this feature's own symbol tiles. `♡` (U+2661) and `☽`
   * (U+263D) are `Extended_Pictographic`, so WITHOUT this list the filter would
   * delete the buyer's own symbol the instant they pressed the tile. The
   * remaining three are `©`, `®` and `™` — also `Extended_Pictographic`, also
   * carried by every Latin engraving face, and a plausible thing to engrave.
   *
   * Kept in sync by `symbols — every symbol tile survives stripEmoji` in
   * engraving-modal.test.js, which reads the real ENGV_SYMBOL_DEFS: adding a
   * seventh tile that happens to be pictographic fails there rather than
   * shipping a tile the input silently eats.
   */
  var ENGRAVABLE_PICTOGRAPHS = '∞♡✩❀☽·©®™';

  /**
   * The draft with every emoji removed. Returns its input untouched when there
   * was nothing to strip, so the caller's `$watch` cannot re-enter — the same
   * contract truncateToMax keeps, and for the same reason.
   */
  function stripEmoji(text) {
    var value = text || '';
    var next = value.replace(EMOJI_RE, function (match) {
      return ENGRAVABLE_PICTOGRAPHS.indexOf(match) >= 0 ? match : '';
    });
    return next === value ? value : next;
  }

  /**
   * "1-6 Characters for RM30.00, 7-12 Characters for RM50.00" — assembled
   * from the live tier table rather than a translated string with prices
   * baked in (design-spec §9).
   */
  function formatTierNote(template, tiers) {
    if (!template) return '';
    return (tiers || [])
      .map(function (t) {
        return template
          .replace('[min]', t.min)
          .replace('[max]', t.max)
          .replace('[price]', t.priceFormatted);
      })
      .join(', ');
  }

  /**
   * The line-item properties an engraved cart line carries (issue #22).
   *
   * Shopify keys a cart line on variant id + the full properties hash, so
   * this object IS the line identity: two adds of the same variant merge
   * into one line at a higher quantity when it deep-equals, and split into
   * two separate lines when it doesn't. Centralised here (rather than
   * rebuilt inline at every call site) so the PDP add and the cart-line
   * edit can never drift into producing subtly different hashes for what
   * the buyer sees as the same engraving.
   *
   * Text is not trimmed: leading/trailing whitespace is billed by
   * countCharacters, so "ALPHA" and "ALPHA " are two distinct engravings
   * (different price tier once the space tips the count) and must stay
   * two distinct cart lines.
   */
  /**
   * Key of the per-line identity tag. Underscore-prefixed so Shopify keeps it
   * out of the cart, checkout and the customer's order confirmation — it is
   * plumbing, not something the buyer engraved.
   */
  var ENGRAVING_UID_KEY = '_engraving_uid';

  /**
   * Which face carries the engraving. Buyer-visible (no underscore) on
   * purpose: the buyer should be able to check front-vs-back on their own
   * order without asking, and the workshop reads it straight off the admin
   * order page rather than having to decode the proof recipe.
   */
  var ENGRAVING_SIDE_KEY = 'Engraving Side';

  /**
   * The layout recipe (see encodeLayout). Underscore-prefixed so Shopify keeps
   * it out of the cart, checkout and the order confirmation — it is a machine
   * string, not something to show a buyer — while still rendering it on the
   * admin Order details page, which is exactly where the workshop needs it
   * (shopify.dev, Ajax Cart API: properties prefixed with `_` are hidden from
   * the customer but retained on the order).
   */
  var ENGRAVING_PROOF_KEY = '_engraving_proof';

  function engravingProperties(text, fontLabel, otherProperties, uid, extras) {
    var props = Object.assign({}, otherProperties || {}, {
      Engraving: text || '',
      'Engraving Font': fontLabel || ''
    });
    if (uid) props[ENGRAVING_UID_KEY] = uid;
    // A blank extra is dropped rather than written as an empty property: an
    // empty value still changes the line's identity hash, which would split
    // what the buyer sees as one engraving across two cart lines (and charge
    // one fee against it). Absent and blank must therefore hash identically.
    if (extras) {
      Object.keys(extras).forEach(function (key) {
        var value = extras[key];
        if (value === null || value === undefined) return;
        if (typeof value === 'string' && !value.trim()) return;
        props[key] = value;
      });
    }
    return props;
  }


  /**
   * One engraving per cart line: an engraved line is deliberately never
   * merged with another, even when the text, font and variant all match.
   *
   * Without this tag Shopify would fold a second identical add into the first
   * line at quantity 2 — and a quantity-2 engraved line is a state the buyer
   * cannot get out of, because both rings then share one editable text while
   * only one engraving fee is charged. Tagging each add with a value nothing
   * else will ever produce keeps every engraving its own line, separately
   * editable and separately billed.
   *
   * Not a UUID on purpose: this only has to be unique against the other lines
   * in one cart, and `crypto.randomUUID` is unavailable on insecure origins,
   * which includes the local dev server.
   */
  function newEngravingUid() {
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /** Shared numeric clamp. Deliberately private: nothing outside needs it. */
  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  var LAYOUT_VERSION = 'v1';

  /**
   * The engraving, flattened to one line so it fits a Shopify line item
   * property (strings only) and survives cart -> checkout -> order -> admin.
   *
   *   v1|<templateHandle>|<side>|<x>|<y>|<scale>|<fontFamily>|<text>
   *
   * `x`/`y`/`scale` are the TEMPLATE's own centre and 1.00. They are not a
   * buyer choice — the buyer has never been able to move or resize the text
   * (the advanced placement controls were withdrawn 2026-09-03, see CLAUDE.md
   * § "Engraving placement is fixed by the template") — and the renderer
   * ignores all three, re-reading text_x / text_y / text_width / text_height
   * live off the template instead. They stay in the wire format because every
   * recipe already sitting on a paid order carries them in these positions and
   * decodeLayout requires them to be numeric.
   *
   * Recipes written between 2026-08 and 2026-09-03 carry up to SEVEN more
   * segments (orientation, alignX, four frame numbers, alignY). They still
   * decode: extra segments are ignored, which is what lets the format shrink
   * without a v2 exactly as it grew without one.
   *
   * The font is identified by its CSS family, not by its buyer-facing label:
   * the label is merchant-editable free text (renaming "Romance" would orphan
   * every past order), while the family is the stable key the renderer actually
   * needs to draw the face. The readable name is already on the line separately
   * as the `Engraving Font` property, so nothing is lost.
   *
   * Pipe-delimited rather than JSON because the value is read by humans on the
   * admin order page and JSON braces/quotes get mangled by copy-paste; the
   * leading version tag exists so a v2 can be introduced without having to
   * guess at the shape of recipes already sitting in historical orders.
   *
   * The two free-text segments are percent-encoded because a buyer's text
   * legitimately contains the delimiter itself, plus `#`, `%`, CJK and the six
   * engraving symbols. encodeURIComponent escapes `|` to `%7C`, which is what
   * makes splitting on `|` safe at all.
   */
  function encodeLayout(layout) {
    var l = layout || {};
    return [
      LAYOUT_VERSION,
      l.templateHandle || '',
      // Normalised rather than passed through, so encode can never emit a
      // recipe its own decoder rejects — a rejected recipe loses the buyer's
      // engraving silently, on an order that has already been paid for.
      l.side === 'back' ? 'back' : 'front',
      Math.round(finiteOr(l.x, 500)),
      Math.round(finiteOr(l.y, 500)),
      finiteOr(l.scale, 1).toFixed(2),
      encodeURIComponent(l.fontFamily || ''),
      encodeURIComponent(l.text || '')
    ].join('|');
  }

  /** Same reason as the `side` normalisation above: never emit `NaN`. */
  function finiteOr(value, fallback) {
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  /**
   * Number('') and Number('  ') are both 0, so a truncated recipe would decode
   * a missing coordinate as a legitimate origin. A blank segment is corruption,
   * not a zero.
   */
  function recipeNumber(segment) {
    if (!segment || !segment.trim()) return NaN;
    var n = Number(segment);
    return isFinite(n) ? n : NaN;
  }

  /**
   * Parse a recipe that has round-tripped through a cart, an order and quite
   * possibly a merchant's copy-paste. Untrusted input: never throws, and never
   * returns a half-populated object — a caller that gets a non-null result can
   * render from it without re-validating a single field.
   *
   * Out-of-range numbers are clamped rather than rejected because a coordinate
   * that is merely too large is recoverable (the engraving still renders, just
   * pulled back inside the template) whereas throwing the whole recipe away
   * loses information nothing else in the order carries.
   */
  function decodeLayout(str) {
    if (!str || typeof str !== 'string') return null;
    var parts = str.split('|');
    // Extra segments are ignored, not rejected. That is load-bearing in both
    // directions now: it let the format GROW to 15 segments in 2026-08, and it
    // is what lets those 15-segment recipes — sitting on paid orders — still
    // decode after the placement controls were withdrawn and the format shrank
    // back to 8.
    if (parts.length < 8) return null;
    if (parts[0] !== LAYOUT_VERSION) return null;
    if (parts[2] !== 'front' && parts[2] !== 'back') return null;
    var x = recipeNumber(parts[3]);
    var y = recipeNumber(parts[4]);
    var scale = recipeNumber(parts[5]);
    if (isNaN(x) || isNaN(y) || isNaN(scale)) return null;
    var fontFamily;
    var text;
    try {
      // Throws URIError on a malformed escape ("%", "%zz") — the shape a
      // truncated or hand-edited property value takes most often.
      fontFamily = decodeURIComponent(parts[6]);
      text = decodeURIComponent(parts[7]);
    } catch (e) {
      return null;
    }
    return {
      version: parts[0],
      templateHandle: parts[1],
      side: parts[2],
      x: clamp(x, 0, 1000),
      y: clamp(y, 0, 1000),
      // Clamped to the range the encoder could ever have emitted, so a
      // hand-edited or truncated value cannot come back as a size the renderer
      // would then draw. Every recipe written since 2026-09-03 stores exactly
      // 1.00 and the renderer ignores it anyway; the bounds are here for the
      // orders placed while the withdrawn +/- control could still change it.
      scale: clamp(scale, 0.1, 4),
      fontFamily: fontFamily,
      text: text
    };
  }

  /**
   * Link to the shareable proof page for a recipe.
   *
   * The recipe goes in the fragment, not a query string, deliberately: a
   * fragment is never sent to the server, so the buyer's engraving text stays
   * out of access logs and out of any analytics tool that records query
   * strings. `rootUrl` is Shopify.routes.root ('/' single-locale, '/en-my/'
   * localised) which always ends in a slash — the guard is for the call site
   * that forgets it, not for Shopify.
   */
  function proofUrl(rootUrl, recipe) {
    var root = rootUrl || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'pages/engraving-proof#' + (recipe || '');
  }

  /** Read a recipe back off a proof link. Same never-throw contract as decodeLayout. */
  function parseProofUrl(url) {
    if (!url || typeof url !== 'string') return null;
    var hash = url.indexOf('#');
    if (hash === -1) return null;
    return decodeLayout(url.slice(hash + 1));
  }

  var EngravingLogic = {
    countCharacters: countCharacters,
    resolveTier: resolveTier,
    canCommitEngraving: canCommitEngraving,
    wouldExceedMax: wouldExceedMax,
    truncateToMax: truncateToMax,
    stripEmoji: stripEmoji,
    formatTierNote: formatTierNote,
    engravingProperties: engravingProperties,
    newEngravingUid: newEngravingUid,
    encodeLayout: encodeLayout,
    decodeLayout: decodeLayout,
    proofUrl: proofUrl,
    parseProofUrl: parseProofUrl,
    ENGRAVING_UID_KEY: ENGRAVING_UID_KEY,
    ENGRAVING_SIDE_KEY: ENGRAVING_SIDE_KEY,
    ENGRAVING_PROOF_KEY: ENGRAVING_PROOF_KEY
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = EngravingLogic;
  } else {
    root.EngravingLogic = EngravingLogic;
  }
})(typeof window !== 'undefined' ? window : globalThis);
